import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../ui';
import { getTestTime } from './querys';
const formatTime = (sec) => {
    if (!isFinite(sec) || sec < 0)
        sec = 0;
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};
// ────────────────────────────────────────────────────────────────────────────
// 모듈 레벨 싱글톤 오디오
//  - 실제 <audio>를 React 트리 밖의 단일 엘리먼트(new Audio)로 유지한다.
//  - UI(AudioPlayer)는 모달 안/좌상단 어디로 옮겨가며 마운트/언마운트되어도 이 싱글톤에
//    바인딩만 갈아끼우므로, 위치가 바뀌어도 재생이 끊기지 않는다(완전 무중단).
//  - 오프셋/시작은 최초 1회만(startedRef) 수행하고, 이후 마운트는 현재 상태만 반영.
// ────────────────────────────────────────────────────────────────────────────
let sharedAudio = null;
let sharedTestId = null;
let sharedStarted = false;
const getSharedAudio = (testId, src) => {
    if (sharedAudio && sharedTestId === testId)
        return sharedAudio;
    // 다른 테스트로 바뀌면 기존 오디오 정리 후 새로 생성
    if (sharedAudio) {
        try {
            sharedAudio.pause();
        }
        catch (e) { }
    }
    sharedAudio = new Audio(src);
    sharedAudio.preload = 'metadata';
    sharedTestId = testId;
    sharedStarted = false;
    return sharedAudio;
};
// 테스트 종료/퇴장 시 싱글톤 정리 (재생 중지)
export const clearMockAudio = () => {
    if (sharedAudio) {
        try {
            sharedAudio.pause();
        }
        catch (e) { }
    }
    sharedAudio = null;
    sharedTestId = null;
    sharedStarted = false;
};
// @ts-ignore
const AudioPlayerStyled = styled.div `
	width: 100%;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 12px 20px;
	background: #f4f5f7;
	border-radius: 12px;
	margin-bottom: 16px;

	* {
		box-sizing: border-box;
	}

	.play-btn {
		flex: 0 0 auto;
		width: 44px;
		height: 44px;
		border-radius: 50%;
		border: none;
		background: #2b2f38;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}
	.play-btn:disabled {
		cursor: default;
		opacity: 0.92;
	}

	/* 구간 이동(seek) 차단: 클릭/드래그 불가, 표시 전용 */
	.progress {
		flex: 1 1 auto;
		min-width: 0;
		height: 6px;
		border-radius: 3px;
		background: #d8dbe0;
		position: relative;
		pointer-events: none;
	}
	.progress .bar {
		position: absolute;
		left: 0;
		top: 0;
		height: 100%;
		border-radius: 3px;
		background: #2b2f38;
	}
	.progress .knob {
		position: absolute;
		top: 50%;
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: #2b2f38;
		transform: translate(-50%, -50%);
	}

	.time {
		flex: 0 0 auto;
		font-size: 14px;
		color: #4d4d4d;
		white-space: nowrap;
	}

	.volume {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.volume .volume-btn {
		border: none;
		background: transparent;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}
	.volume .volume-input {
		-webkit-appearance: none;
		appearance: none;
		width: 90px;
		height: 6px;
		border-radius: 3px;
		background: #d8dbe0;
		cursor: pointer;
	}
	.volume .volume-input:focus {
		outline: none;
	}
	.volume .volume-input::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #2b2f38;
		cursor: pointer;
	}
	.volume .volume-input::-moz-range-thumb {
		width: 14px;
		height: 14px;
		border: none;
		border-radius: 50%;
		background: #2b2f38;
		cursor: pointer;
	}

	/* 좁은 모바일: 간격/여백·시간 폰트 축소로 사운드 영역이 잘리지 않게 */
	@media (max-width: 480px) {
		gap: 10px;
		padding: 10px 14px;

		.time {
			font-size: 12px;
		}
	}

	/* 가장 좁은 해상도: 볼륨 슬라이더 숨김(음소거 아이콘은 유지) — 잘림 방지 */
	@media (max-width: 400px) {
		gap: 8px;
		padding: 10px 12px;

		.volume {
			gap: 0;
		}
		.volume .volume-input {
			display: none;
		}
	}
`;
/**
 * 모의고사 듣기 mp3 플레이어 (UI)
 * - 실제 재생은 모듈 싱글톤(sharedAudio)이 담당 → UI가 모달 안/좌상단으로 옮겨가도 무중단.
 * - 최초 '테스트 시작' 시 처음(0)부터, 재입장 시 (duration*60 - remaining) 경과 지점부터 재생.
 * - 구간 이동(seek)/일시정지 차단(연속 재생). autoplay 차단 시에만 1회 재생 탭 허용.
 */
export const AudioPlayer = React.memo(({ src, testId, duration }) => {
    const audio = useMemo(() => getSharedAudio(testId, src), [testId, src]);
    const [current, setCurrent] = useState(audio.currentTime || 0);
    const [total, setTotal] = useState(isFinite(audio.duration) ? audio.duration : 0);
    const [playing, setPlaying] = useState(!audio.paused);
    const [needGesture, setNeedGesture] = useState(false);
    const [volume, setVolume] = useState(audio.volume ?? 1);
    const lastVolumeRef = useRef(audio.volume || 1);
    // 서버 타이머 기준 경과 오프셋 계산 → seek → 재생 시도.
    // 최초 시작·재입장·차단 해제(재생 탭) 어느 시점에 불려도 항상 서버 remaining 으로
    // 오프셋을 다시 계산하므로 안전하게 재시도할 수 있다.
    const startPlayback = useCallback(async () => {
        let offset = 0;
        try {
            const res = await getTestTime(testId);
            const remaining = res?.result?.remaining;
            const common = res?.result?.common;
            // common===false(개별 타이머 존재): 경과 = duration*60 - remaining. 그 외(최초): 0.
            if (common === false && typeof remaining === 'number' && remaining >= 0) {
                offset = Math.max(0, duration * 60 - remaining);
            }
        }
        catch (e) { }
        const apply = () => {
            const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : duration * 60;
            try {
                audio.currentTime = Math.min(Math.max(0, offset), Math.max(0, dur - 0.3));
            }
            catch (e) { }
            audio
                .play()
                .then(() => setNeedGesture(false))
                .catch(() => setNeedGesture(true));
        };
        if (audio.readyState >= 1)
            apply();
        else {
            audio.addEventListener('loadedmetadata', apply, { once: true });
            // 메타데이터가 끝내 로드되지 않으면(네트워크/소스 오류) 재생 탭으로 전체 재시도를
            // 열어둔다. (기존: 이 경우 needGesture 가 영영 false → 버튼 비활성 + 0:00 고정 → 시작 불가)
            window.setTimeout(() => {
                if (audio.readyState < 1)
                    setNeedGesture(true);
            }, 4000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audio, testId, duration]);
    // 최초 1회: 오프셋 적용 + 재생 시작 (싱글톤 단위로 1회만)
    useEffect(() => {
        if (sharedStarted) {
            // 이미 시작된 싱글톤이 정지 상태로 남아 있으면(이전 화면에서 차단·오류 등) 재개를 시도하고,
            // 실패하면 재생 탭을 열어 사용자가 직접 이어 재생할 수 있게 한다.
            if (audio.paused && !audio.ended) {
                audio
                    .play()
                    .then(() => setNeedGesture(false))
                    .catch(() => setNeedGesture(true));
            }
            return;
        }
        sharedStarted = true;
        startPlayback();
        // testId 단위 1회 (싱글톤 sharedStarted 로 가드)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId]);
    // UI 상태 동기화: 마운트마다 싱글톤에 리스너 부착, 언마운트 시 제거(오디오는 정지하지 않음)
    useEffect(() => {
        const onTime = () => {
            setCurrent(audio.currentTime);
            if (isFinite(audio.duration))
                setTotal(audio.duration);
        };
        const onPlay = () => setPlaying(true);
        // 일시정지 차단: 종료 전 pause 되면 즉시 재개
        const onPause = () => {
            if (!audio.ended) {
                audio.play().catch(() => { });
            }
            else {
                setPlaying(false);
            }
        };
        const onEnded = () => setPlaying(false);
        // 소스 로드 오류 시 재생 탭을 열어 수동 재시도(startPlayback: load + seek + play) 가능하게 한다.
        const onError = () => setNeedGesture(true);
        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('playing', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('error', onError);
        // 현재 상태 즉시 반영
        setCurrent(audio.currentTime);
        setPlaying(!audio.paused);
        if (isFinite(audio.duration))
            setTotal(audio.duration);
        setVolume(audio.volume ?? 1);
        return () => {
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('playing', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
            // 싱글톤이므로 정지/해제하지 않음 (위치 전환 시 무중단 유지)
        };
    }, [audio]);
    const applyVolume = useCallback((v) => {
        setVolume(v);
        audio.volume = v;
    }, [audio]);
    const onVolume = (e) => {
        applyVolume(Number(e.target.value));
    };
    const toggleMute = () => {
        if (volume > 0) {
            lastVolumeRef.current = volume;
            applyVolume(0);
        }
        else {
            applyVolume(lastVolumeRef.current > 0 ? lastVolumeRef.current : 1);
        }
    };
    const totalForDisplay = total > 0 ? total : duration * 60;
    const pct = totalForDisplay > 0 ? Math.min(100, (current / totalForDisplay) * 100) : 0;
    return (React.createElement(AudioPlayerStyled, null,
        React.createElement("button", { type: "button", className: "play-btn", 
            // 재생 중에는 비활성(일시정지 불가). autoplay 차단/로드 오류 시에만 재생 탭 허용.
            disabled: !needGesture, onClick: () => {
                if (!needGesture)
                    return;
                // 차단·오류 후 수동 시작: 경과 오프셋을 서버 타이머 기준으로 다시 계산해 이어 재생.
                // (차단된 동안에도 서버 타이머는 진행되므로 단순 play()면 위치가 뒤처진다)
                if (audio.readyState < 1) {
                    try {
                        audio.load();
                    }
                    catch (e) { }
                }
                startPlayback();
            } },
            React.createElement(Icon, { icon: playing ? 'vodPause' : 'vodStart', size: 20, color: "#ffffff" })),
        React.createElement("div", { className: "progress" },
            React.createElement("div", { className: "bar", style: { width: `${pct}%` } }),
            React.createElement("div", { className: "knob", style: { left: `${pct}%` } })),
        React.createElement("div", { className: "time" },
            formatTime(current),
            " / ",
            formatTime(totalForDisplay)),
        React.createElement("div", { className: "volume" },
            React.createElement("button", { type: "button", className: "volume-btn", onClick: toggleMute },
                React.createElement(Icon, { icon: volume === 0 ? 'vodVolumeOff' : 'vodVolumeOn', size: 20, color: "#4d4d4d" })),
            React.createElement("input", { type: "range", className: "volume-input", min: 0, max: 1, step: 0.05, value: volume, onChange: onVolume }))));
});
